import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Clock3,
  RotateCcw,
  Truck,
} from "lucide-react";
import { useId } from "react";
import {
  dropshipV2ErrorMessage,
  type DropshipV2Field,
  type DropshipV2FreightReview,
  usePatchDropshipV2Field,
  useRetryDropshipV2FreightReview,
} from "@/api/dropship-v2";
import { LoadingButton } from "@/components/loading-button";
import { POInlineEditableField } from "@/components/po-inline-edit";
import { ReviewChoiceGroup } from "@/components/review-choice-group";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatMoney, formatPercent, formatStatus } from "@/lib/utils";

const MODE_OPTIONS = [
  { value: "imported", label: "Use imported freight" },
  { value: "requote", label: "Requote manufacturers" },
] as const;

const DOCK_OPTIONS = [
  { value: "true", label: "Dock / forklift available" },
  { value: "false", label: "No dock" },
] as const;

const DELIVERY_DEFAULTS = [
  {
    key: "limited_access_delivery",
    label: "Limited-access delivery",
  },
  {
    key: "inside_delivery",
    label: "Inside delivery",
  },
  {
    key: "residential_delivery",
    label: "Destination type",
  },
  {
    key: "appointment_delivery",
    label: "Delivery appointment",
  },
  {
    key: "construction_site_delivery",
    label: "Construction-site delivery",
  },
] as const;

type DeliveryDefaultKey = (typeof DELIVERY_DEFAULTS)[number]["key"];
type AccessorialKey = "liftgate_delivery" | DeliveryDefaultKey;

const ACCESSORIAL_KEYS: readonly AccessorialKey[] = [
  "liftgate_delivery",
  ...DELIVERY_DEFAULTS.map(({ key }) => key),
];

const REQUIREMENT_OPTIONS = [
  { value: "true", label: "Required" },
  { value: "false", label: "Not required" },
] as const;

const DESTINATION_TYPE_OPTIONS = [
  { value: "true", label: "Residential" },
  { value: "false", label: "Commercial" },
] as const;

type CommercialFreightPricing = {
  mode: "imported" | "requote";
  ready: boolean;
  freightNet: string | null;
  freightSell: string | null;
  importedFreightSell: string | null;
  hasLoadingDock: boolean | null;
  groups: Array<{
    groupingKey: string;
    manufacturerName: string;
    freightNet: string;
    freightSell: string;
    markupPercent: string;
    method: string;
    source: string;
    bookingParty: string;
    isFree: boolean;
    zeroLabel: string | null;
  }>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value != null && !Array.isArray(value);
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : null;
}

function booleanValue(value: unknown): boolean {
  return value === true;
}

function booleanOrNull(value: unknown): boolean | null {
  return value === true || value === false ? value : null;
}

function normalizeGroup(
  value: unknown,
): CommercialFreightPricing["groups"][number] | null {
  if (!isRecord(value)) return null;
  const manufacturer = isRecord(value.manufacturer) ? value.manufacturer : {};
  const groupingKey = stringOrNull(value.grouping_key);
  const freightNet = stringOrNull(value.freight_net);
  const freightSell = stringOrNull(value.freight_sell);
  const markupPercent = stringOrNull(value.markup_percent);
  if (
    !groupingKey ||
    freightNet == null ||
    freightSell == null ||
    markupPercent == null
  )
    return null;
  return {
    groupingKey,
    manufacturerName: stringOrNull(manufacturer.name) || "Manufacturer",
    freightNet,
    freightSell,
    markupPercent,
    method: stringOrNull(value.method) || "",
    source: stringOrNull(value.source) || "",
    bookingParty: stringOrNull(value.booking_party) || "",
    isFree: booleanValue(value.is_free),
    zeroLabel: stringOrNull(value.zero_label),
  };
}

function pricingFromReview(
  review: DropshipV2FreightReview,
): CommercialFreightPricing {
  const pricing = review.pricing;
  return {
    mode: pricing.mode,
    ready: pricing.ready,
    freightNet: pricing.freight_net ?? null,
    freightSell: pricing.freight_sell ?? null,
    importedFreightSell: pricing.imported_freight_sell ?? null,
    hasLoadingDock: pricing.has_loading_dock ?? null,
    groups: (pricing.groups ?? [])
      .map(normalizeGroup)
      .filter((group) => group != null),
  };
}

function pricingFromSnapshot(
  snapshot: Record<string, unknown>,
): CommercialFreightPricing | null {
  const mode = snapshot.mode;
  if (mode !== "imported" && mode !== "requote") return null;
  return {
    mode,
    ready: snapshot.ready === true,
    freightNet: stringOrNull(snapshot.freight_net),
    freightSell: stringOrNull(snapshot.freight_sell),
    importedFreightSell: stringOrNull(snapshot.imported_freight_sell),
    hasLoadingDock: booleanOrNull(snapshot.has_loading_dock),
    groups: Array.isArray(snapshot.groups)
      ? snapshot.groups.map(normalizeGroup).filter((group) => group != null)
      : [],
  };
}

function settingField(fields: DropshipV2Field[], key: string) {
  return fields.find((field) => field.field_key === key);
}

function settingText(field: DropshipV2Field | undefined): string {
  return stringOrNull(field?.value) ?? "";
}

function settingRecord(
  field: DropshipV2Field | undefined,
): Record<string, string> {
  if (!isRecord(field?.value)) return {};
  return Object.fromEntries(
    Object.entries(field.value).flatMap(([key, value]) => {
      const normalized = stringOrNull(value);
      return normalized == null ? [] : [[key, normalized]];
    }),
  );
}

function requirementValue(
  field: DropshipV2Field | undefined,
  key: AccessorialKey,
): boolean | null {
  const value = isRecord(field?.value) ? field.value : {};
  const requirements = isRecord(value.requirements) ? value.requirements : {};
  const raw = requirements[key];
  const decision = isRecord(raw) ? raw.value : raw;
  if (decision === true || decision === false) return decision;
  return null;
}

function snapshotLabel(
  snapshot: Record<string, unknown> | undefined,
  fallbackKey: string,
  fallback: string,
) {
  const labelKeys = ["label", "name", "display_name", "site_name", "title"];
  for (const key of labelKeys) {
    const label = stringOrNull(snapshot?.[key])?.trim();
    if (label) return label;
  }
  const keyLabel = formatStatus(fallbackKey);
  return keyLabel && keyLabel !== "-" ? keyLabel : fallback;
}

function PricingTotals({ pricing }: { pricing: CommercialFreightPricing }) {
  return (
    <dl className="grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-4">
      <div>
        <dt className="text-xs font-medium text-muted-foreground">
          Pricing mode
        </dt>
        <dd className="mt-1 text-sm font-semibold">
          {pricing.mode === "imported"
            ? "Imported quote freight"
            : "Manufacturer requote"}
        </dd>
      </div>
      <div>
        <dt className="text-xs font-medium text-muted-foreground">
          Freight Net
        </dt>
        <dd className="mt-1 text-sm font-semibold tabular-nums">
          {pricing.freightNet == null ? "-" : formatMoney(pricing.freightNet)}
        </dd>
      </div>
      <div>
        <dt className="text-xs font-medium text-muted-foreground">
          Freight Sell
        </dt>
        <dd className="mt-1 text-sm font-semibold tabular-nums">
          {pricing.freightSell == null
            ? "Pending"
            : formatMoney(pricing.freightSell)}
        </dd>
      </div>
      <div>
        <dt className="text-xs font-medium text-muted-foreground">
          Delivery site
        </dt>
        <dd className="mt-1 text-sm font-semibold">
          {pricing.hasLoadingDock == null
            ? "Needs answer"
            : pricing.hasLoadingDock
              ? "Loading dock available"
              : "No loading dock"}
        </dd>
      </div>
    </dl>
  );
}

export function ApprovedFreightPricing({
  snapshot,
  className,
}: {
  snapshot: Record<string, unknown>;
  className?: string;
}) {
  const pricing = pricingFromSnapshot(snapshot);
  if (!pricing) {
    return (
      <div
        className={cn(
          "rounded-lg border border-destructive/30 bg-destructive/5 p-3",
          className,
        )}
      >
        <p className="flex items-center gap-2 text-sm font-medium text-destructive">
          <AlertCircle className="size-4" aria-hidden="true" /> Approved
          commercial freight is unavailable
        </p>
      </div>
    );
  }

  const group = pricing.groups[0];
  return (
    <section
      aria-label="Approved customer freight pricing"
      className={cn("space-y-3", className)}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold">
            Approved customer freight pricing
          </h4>
        </div>
        <Badge variant="outline">
          {pricing.mode === "imported" ? "Imported" : "Requoted"}
        </Badge>
      </div>
      <PricingTotals pricing={pricing} />
      {group ? (
        <div className="flex flex-wrap gap-x-5 gap-y-1 border-t pt-3 text-xs text-muted-foreground">
          <span>{formatPercent(group.markupPercent)} markup</span>
          <span>
            {group.isFree
              ? group.zeroLabel || "Manufacturer free freight"
              : formatStatus(group.method)}
          </span>
          <span>
            {group.bookingParty === "manufacturer" ? "Manufacturer" : "USA-RS"}
          </span>
        </div>
      ) : null}
    </section>
  );
}

function statusIcon(state: string) {
  if (state === "ready")
    return (
      <CheckCircle2 className="size-4 text-emerald-600" aria-hidden="true" />
    );
  if (["pending", "running"].includes(state)) {
    return (
      <Clock3 className="size-4 text-muted-foreground" aria-hidden="true" />
    );
  }
  return <AlertCircle className="size-4 text-destructive" aria-hidden="true" />;
}

export function DropshipFreightReview({
  draftUuid,
  fields,
  review,
  loading,
  fetching,
  error,
  disabled = false,
  onReload,
}: {
  draftUuid: string;
  fields: DropshipV2Field[];
  review?: DropshipV2FreightReview;
  loading: boolean;
  fetching: boolean;
  error: unknown;
  disabled?: boolean;
  onReload: () => void;
}) {
  const patchField = usePatchDropshipV2Field(draftUuid);
  const retry = useRetryDropshipV2FreightReview(draftUuid);
  const modeField = settingField(fields, "freight.pricing_mode");
  const dockField = settingField(fields, "freight.has_loading_dock");
  const requirementsField = settingField(fields, "freight.requirements");
  const defaultMarkupField = settingField(
    fields,
    "freight.default_markup_percent",
  );
  const modeValue = settingText(modeField);
  const mode =
    modeValue === "requote" || modeValue === "imported"
      ? modeValue
      : review?.pricing.mode;
  const hasLoadingDock =
    dockField?.value === true || dockField?.value === false
      ? dockField.value
      : null;
  const deliveryDefaults = Object.fromEntries(
    DELIVERY_DEFAULTS.map(({ key }) => [
      key,
      requirementValue(requirementsField, key),
    ]),
  ) as Record<DeliveryDefaultKey, boolean | null>;
  const defaultMarkup = settingText(defaultMarkupField) || "20.00";
  const pricing =
    review && review.pricing.mode === mode
      ? { ...pricingFromReview(review), hasLoadingDock }
      : undefined;
  const canRetry =
    mode === "requote" &&
    review?.pricing.mode === "requote" &&
    !fetching &&
    !retry.isPending;
  const fieldPending = (field: DropshipV2Field | undefined) =>
    Boolean(
      patchField.isPending &&
      field &&
      patchField.variables?.field.uuid === field.uuid,
    );

  const patch = (field: DropshipV2Field | undefined, value: unknown) => {
    if (!field || disabled) return;
    patchField.mutate({ field, value });
  };
  const requirementValueWithChoice = (
    field: DropshipV2Field,
    key: DeliveryDefaultKey,
    value: boolean,
  ) => {
    const current = isRecord(field.value)
      ? field.value
      : {};
    const requirements = isRecord(current.requirements)
      ? current.requirements
      : {};
    const normalizedRequirements = {} as Record<
      AccessorialKey,
      Record<string, unknown>
    >;
    for (const requirementKey of ACCESSORIAL_KEYS) {
      const raw = requirements[requirementKey];
      const metadata = isRecord(raw) ? raw : {};
      let decision = requirementValue(field, requirementKey);
      let nextMetadata = metadata;
      if (
        requirementKey === "liftgate_delivery" &&
        metadata.source === "loading_dock_derived"
      ) {
        decision = null;
      }
      if (requirementKey === key) {
        decision = value;
        nextMetadata = { ...metadata, source: "sales_review" };
      } else if (
        requirementKey === "liftgate_delivery" &&
        decision == null &&
        hasLoadingDock != null
      ) {
        decision = !hasLoadingDock;
        nextMetadata = {
          ...metadata,
          source: "loading_dock_derived",
          derived_from: "freight.has_loading_dock",
          has_loading_dock: hasLoadingDock,
          rule: "liftgate_delivery = not has_loading_dock",
        };
      }
      normalizedRequirements[requirementKey] = {
        ...nextMetadata,
        value: decision,
      };
    }
    const unknown = ACCESSORIAL_KEYS.filter(
      (requirementKey) => normalizedRequirements[requirementKey].value == null,
    );
    return {
      ...current,
      status: unknown.length ? "needs_review" : "complete",
      reason: unknown.length ? "freight_requirements_unknown" : null,
      unknown,
      requirements: normalizedRequirements,
    };
  };
  const patchRequirement = (key: DeliveryDefaultKey, value: boolean) => {
    if (!requirementsField || disabled || fieldPending(requirementsField))
      return;
    patchField.mutate({
      field: requirementsField,
      value: requirementValueWithChoice(requirementsField, key, value),
      reapplyOnStale: (currentField) =>
        requirementValueWithChoice(currentField, key, value),
    });
  };

  return (
    <section
      aria-labelledby="freight-review-title"
      className="overflow-hidden rounded-xl border bg-background"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b bg-muted/20 px-4 py-3">
        <div>
          <div className="flex items-center gap-2">
            <Truck
              className="size-4 text-muted-foreground"
              aria-hidden="true"
            />
            <h2 id="freight-review-title" className="text-sm font-semibold">
              Customer freight pricing
            </h2>
          </div>
        </div>
        {review && !review.ready ? (
          <Badge variant="secondary">
            {review.blocking_group_count} blocker
            {review.blocking_group_count === 1 ? "" : "s"}
          </Badge>
        ) : null}
      </div>

      <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(14rem,0.8fr)]">
        <div className="min-w-0 space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            Commercial pricing source
          </p>
          <ReviewChoiceGroup
            label="Commercial freight pricing source"
            value={mode ?? "imported"}
            options={MODE_OPTIONS}
            className="grid w-full grid-cols-2"
            optionClassName="min-h-11 w-full justify-center sm:min-h-9"
            disabled={disabled || fieldPending(modeField) || !modeField}
            onChange={(value) => patch(modeField, value)}
          />
        </div>
        <div className="min-w-0 space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            Delivery site
          </p>
          <ReviewChoiceGroup
            label="Loading dock availability"
            value={hasLoadingDock == null ? "" : String(hasLoadingDock)}
            options={DOCK_OPTIONS}
            className="grid w-full grid-cols-2"
            optionClassName="min-h-11 w-full justify-center sm:min-h-9"
            disabled={disabled || fieldPending(dockField) || !dockField}
            onChange={(value) => patch(dockField, value === "true")}
          />
        </div>
      </div>

      <details className="group border-t">
        <summary className="flex min-h-11 cursor-pointer list-none items-center gap-1.5 px-4 py-2 text-sm font-medium hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50">
          <span>Delivery defaults</span>
          <ChevronDown
            className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>
        <div className="grid gap-x-6 gap-y-4 border-t bg-muted/10 px-4 py-4 sm:grid-cols-2 xl:grid-cols-3">
          {DELIVERY_DEFAULTS.map(({ key, label }) => (
            <div key={key} className="min-w-0 space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">
                {label}
              </p>
              <ReviewChoiceGroup
                label={`${label} default`}
                value={
                  deliveryDefaults[key] == null
                    ? ""
                    : String(deliveryDefaults[key])
                }
                options={
                  key === "residential_delivery"
                    ? DESTINATION_TYPE_OPTIONS
                    : REQUIREMENT_OPTIONS
                }
                className="grid w-full grid-cols-2"
                optionClassName="min-h-11 w-full justify-center px-2 sm:min-h-9"
                disabled={
                  disabled ||
                  fieldPending(requirementsField) ||
                  !requirementsField
                }
                onChange={(value) => patchRequirement(key, value === "true")}
              />
            </div>
          ))}
        </div>
      </details>

      {loading && !review ? (
        <div className="space-y-3 border-t p-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : error && !review ? (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 border-t p-4"
        >
          <p className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="size-4" aria-hidden="true" />
            {dropshipV2ErrorMessage(
              error,
              "Freight pricing could not be verified.",
            )}
          </p>
          <Button type="button" variant="outline" size="sm" onClick={onReload}>
            <RotateCcw className="size-4" /> Retry
          </Button>
        </div>
      ) : !pricing ? (
        <div className="border-t px-4 py-5 text-sm text-muted-foreground">
          Updating the commercial freight projection…
        </div>
      ) : (
        <div className="space-y-4 border-t p-4">
          <PricingTotals pricing={pricing} />

          {mode === "requote" ? (
            <div className="space-y-3 border-t pt-4">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">
                    Default true markup
                  </p>
                  <POInlineEditableField
                    value={defaultMarkup}
                    displayValue={formatPercent(defaultMarkup)}
                    kind="number"
                    showEditIcon
                    disabled={disabled || !defaultMarkupField}
                    pending={fieldPending(defaultMarkupField)}
                    ariaLabel="Edit default freight markup"
                    buttonClassName="px-1 font-semibold tabular-nums"
                    inputClassName="w-24"
                    onConfirm={(value) => patch(defaultMarkupField, value)}
                  />
                </div>
                <LoadingButton
                  type="button"
                  size="sm"
                  loading={retry.isPending}
                  loadingText="Starting requote…"
                  disabled={!canRetry}
                  onClick={() =>
                    review &&
                    retry.mutate({
                      expectedContentRevision: review.content_revision,
                    })
                  }
                >
                  <RotateCcw className="size-4" />
                  {pricing.ready
                    ? "Refresh all freight quotes"
                    : "Get freight quotes"}
                </LoadingButton>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

export function DropshipManufacturerFreightReview({
  draftUuid,
  fields,
  review,
  manufacturerId,
  manufacturerName,
  rootLineUuids,
  disabled = false,
  fetching,
}: {
  draftUuid: string;
  fields: DropshipV2Field[];
  review?: DropshipV2FreightReview;
  manufacturerId: number | null;
  manufacturerName: string;
  rootLineUuids: string[];
  disabled?: boolean;
  fetching: boolean;
}) {
  const patchField = usePatchDropshipV2Field(draftUuid);
  const retry = useRetryDropshipV2FreightReview(draftUuid);
  const retryDescriptionId = useId();
  const defaultMarkup =
    settingText(settingField(fields, "freight.default_markup_percent")) ||
    "20.00";
  const groupMarkupField = settingField(
    fields,
    "freight.group_markup_percentages",
  );
  const groupMarkups = settingRecord(groupMarkupField);
  const rootUuidSet = new Set(rootLineUuids);
  const groups = (review?.groups ?? []).filter((group) => {
    const manufacturerMatches =
      manufacturerId != null
        ? group.manufacturer_id === manufacturerId
        : group.manufacturer_name.trim().toLocaleLowerCase() ===
          manufacturerName.trim().toLocaleLowerCase();
    if (!manufacturerMatches) return false;
    if (!group.root_lines?.length || rootUuidSet.size === 0) return true;
    return group.root_lines.some((line) => rootUuidSet.has(line.uuid));
  });
  const pricingByKey = new Map(
    (review?.pricing.groups ?? []).map((group) => [group.grouping_key, group]),
  );
  const hasUnresolvedGroups = groups.some((group) => group.state !== "ready");
  const canRetry =
    Boolean(review) &&
    hasUnresolvedGroups &&
    !disabled &&
    !fetching &&
    !patchField.isPending &&
    !retry.isPending;
  const patchMarkup = (groupingKey: string, value: string) => {
    if (!groupMarkupField || disabled) return;
    patchField.mutate({
      field: groupMarkupField,
      value: { ...groupMarkups, [groupingKey]: value },
    });
  };

  if (!groups.length) {
    return null;
  }

  return (
    <section
      aria-label={`${manufacturerName} freight review`}
      className="border-t bg-muted/10"
    >
      <div className="divide-y">
        {groups.map((group) => {
          const priced = pricingByKey.get(group.grouping_key);
          const attempt = group.attempt;
          const providerNames = [
            ...new Set(
              (attempt?.calls ?? [])
                .map((call) => call.provider)
                .filter(Boolean),
            ),
          ];
          const offers = (attempt?.calls ?? []).flatMap((call) => call.offers);
          const selectedOffer = offers.find((offer) => offer.is_selected);
          const markup = groupMarkups[group.grouping_key] ?? defaultMarkup;

          return (
            <article key={group.grouping_key} className="space-y-3 px-4 py-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-semibold">
                    {statusIcon(group.state)}
                    {formatStatus(group.state)}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Destination:{" "}
                    {snapshotLabel(
                      group.destination_snapshot,
                      group.destination_key,
                      "Destination",
                    )}
                    {" · "}Release:{" "}
                    {snapshotLabel(
                      group.release_snapshot,
                      group.release_key,
                      "Release group",
                    )}
                  </p>
                  {group.root_lines?.length ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Quote lines{" "}
                      {group.root_lines
                        .map((line) => line.display_number)
                        .join(", ")}
                    </p>
                  ) : null}
                </div>
                {group.blocking ? (
                  <Badge variant="destructive">Blocking</Badge>
                ) : null}
              </div>

              {group.blocker_message ? (
                <p className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  <AlertCircle
                    className="mt-0.5 size-4 shrink-0"
                    aria-hidden="true"
                  />
                  {group.blocker_message}
                </p>
              ) : null}

              <dl className="grid gap-x-5 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <dt className="text-xs text-muted-foreground">Freight Net</dt>
                  <dd className="mt-0.5 text-sm font-semibold tabular-nums">
                    {priced ? formatMoney(priced.freight_net) : "Pending"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">
                    Freight Sell
                  </dt>
                  <dd className="mt-0.5 text-sm font-semibold tabular-nums">
                    {priced ? formatMoney(priced.freight_sell) : "Pending"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">
                    Group markup
                  </dt>
                  <dd>
                    <POInlineEditableField
                      value={markup}
                      displayValue={formatPercent(markup)}
                      kind="number"
                      showEditIcon
                      disabled={disabled || !groupMarkupField}
                      pending={patchField.isPending}
                      ariaLabel={`Edit ${manufacturerName} ${snapshotLabel(group.release_snapshot, group.release_key, "release")} freight markup`}
                      buttonClassName="px-1 font-semibold tabular-nums"
                      inputClassName="w-24"
                      onConfirm={(value) =>
                        patchMarkup(group.grouping_key, value)
                      }
                    />
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">
                    Freight policy
                  </dt>
                  <dd className="mt-0.5 text-sm font-medium">
                    {priced?.is_free
                      ? priced.zero_label || "Manufacturer free freight"
                      : priced?.booking_party === "manufacturer"
                        ? "Manufacturer"
                        : priced
                          ? "USA-RS"
                          : "Pending"}
                  </dd>
                </div>
              </dl>

              {attempt ? (
                <div className="flex flex-wrap gap-x-5 gap-y-1 border-t pt-3 text-xs text-muted-foreground">
                  <span>
                    Attempt #{attempt.id} · {formatStatus(attempt.status)}
                  </span>
                  <span>
                    {attempt.method === "manufacturer_rule"
                      ? "Manufacturer policy fallback"
                      : providerNames.length
                        ? `Provider: ${providerNames.join(", ")}`
                        : "Provider quote pending"}
                  </span>
                  <span>
                    {offers.length} offer{offers.length === 1 ? "" : "s"}
                    {selectedOffer
                      ? ` · selected ${selectedOffer.carrier_name} ${selectedOffer.service_level}`
                      : ""}
                  </span>
                  {attempt.selected_transit_days != null ? (
                    <span>{attempt.selected_transit_days} transit days</span>
                  ) : null}
                  {attempt.error_message ? (
                    <span>{attempt.error_message}</span>
                  ) : null}
                </div>
              ) : (
                <p className="border-t pt-3 text-xs text-muted-foreground">
                  No freight attempt has started for this destination and
                  release.
                </p>
              )}
            </article>
          );
        })}
      </div>
      {hasUnresolvedGroups ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
          <p
            id={retryDescriptionId}
            className="max-w-2xl text-xs text-muted-foreground"
          >
            Requotes every unresolved manufacturer freight group in this draft,
            not only {manufacturerName}.
          </p>
          <LoadingButton
            type="button"
            variant="outline"
            size="sm"
            aria-describedby={retryDescriptionId}
            loading={retry.isPending}
            loadingText="Requoting all unresolved manufacturer freight groups…"
            disabled={!canRetry}
            onClick={() =>
              review &&
              retry.mutate({
                expectedContentRevision: review.content_revision,
              })
            }
          >
            <RotateCcw className="size-4" /> Requote all unresolved manufacturer
            freight groups
          </LoadingButton>
        </div>
      ) : null}
    </section>
  );
}
